"use client";

import clsx from "clsx";
import { Link } from "@repo/next-utils/router";
import { Text } from "@repo/ui/text";
import {
  TYPOGRAPHY_BODY3_BOLD,
  TYPOGRAPHY_LABEL3_BLACK,
  TYPOGRAPHY_SMALLPRINT,
} from "@/theme/topshop/typography/typography-constants";
import type { BannerContent } from "./global-banner.types";
import styles from "./global-banner.module.css";

type GlobalBannerProps = {
  content: BannerContent;
};

export const GlobalBanner = ({ content }: GlobalBannerProps) => {
  const renderSubtitle = () => {
    if (!content.subtitle) return null;

    // Subtitle with inline links (array of segments)
    if (Array.isArray(content.subtitle)) {
      return (
        <span
          className={clsx(
            TYPOGRAPHY_BODY3_BOLD,
            content.colorScheme === "black" && styles.subtitleHighlight,
          )}
          suppressHydrationWarning
        >
          {content.subtitle.map((segment, index) =>
            segment.type === "text" ? (
              <span key={index} suppressHydrationWarning>
                {segment.value}
              </span>
            ) : (
              <Link
                key={index}
                href={segment.href}
                className={styles.subtitleLink}
                suppressHydrationWarning
              >
                {segment.label}
              </Link>
            ),
          )}
        </span>
      );
    }

    // Cyber Monday periods: highlight "NEW CUSTOMERS" in green
    if (content.colorScheme === "cyberMonday") {
      const parts = content.subtitle.split("NEW CUSTOMERS");
      if (parts.length === 2) {
        return (
          <Text className={TYPOGRAPHY_BODY3_BOLD} suppressHydrationWarning>
            <span className={styles.subtitleHighlight}>NEW CUSTOMERS</span>
            {parts[1]}
          </Text>
        );
      }
    }

    // Prevent "(STANDARD SERVICE)" from breaking across lines
    const subtitleWithNonBreakingSpace = content.subtitle.replace(
      /\(STANDARD SERVICE\)/g,
      "(STANDARD\u00A0SERVICE)",
    );

    return (
      <Text
        className={clsx(
          TYPOGRAPHY_BODY3_BOLD,
          content.colorScheme === "black" && styles.subtitleHighlight,
        )}
        suppressHydrationWarning
      >
        {subtitleWithNonBreakingSpace}
      </Text>
    );
  };

  // Centered layout: single-field banners OR title+subtitle banners (subtitle can be string or segments array)
  const hasSubtitle =
    content.subtitle !== undefined &&
    (typeof content.subtitle === "string"
      ? content.subtitle
      : content.subtitle.length > 0);
  const isCenteredLayout =
    [content.title, hasSubtitle, content.text].filter(Boolean).length <= 1 ||
    (content.title && hasSubtitle && !content.text && !content.code);

  const bannerContent = (
    <div
      className={clsx(
        styles.content,
        isCenteredLayout && styles.contentCentered,
      )}
      suppressHydrationWarning
    >
      {content.title && (
        <Text
          className={clsx(
            TYPOGRAPHY_BODY3_BOLD,
            styles.title,
            content.colorScheme === "lastChanceBlackFriday" &&
              styles.titleHighlight,
            content.colorScheme === "cyberMonday" && styles.titleHighlight,
          )}
          suppressHydrationWarning
        >
          {content.title}
        </Text>
      )}
      {renderSubtitle()}

      <Text
        className={clsx(
          TYPOGRAPHY_SMALLPRINT,
          styles.text,
          content.colorScheme === "cyberMonday" && styles.textHighlight,
        )}
        suppressHydrationWarning
      >
        {content.text && content.text}{" "}
        {content.code && (
          <strong
            className={clsx(styles.codeHighlight, TYPOGRAPHY_LABEL3_BLACK)}
            suppressHydrationWarning
          >
            {content.code}
          </strong>
        )}
      </Text>
    </div>
  );

  const bannerClassName = clsx(
    styles.banner,
    styles[content.colorScheme],
    content.clickable && styles.clickable,
  );

  if (content.clickable && content.href) {
    // WARNING: subtitle can be a SubtitleSegment[] (array of objects). If a
    // clickable banner ever uses array subtitles, this produces an
    // "[object Object]" aria-label. Flatten the segments into a plain string
    // before that pattern is used with clickable: true.
    const ariaLabel =
      [content.title, content.subtitle].filter(Boolean).join(" - ") +
      " - Click to view sale items";

    return (
      <Link
        href={content.href}
        className={bannerClassName}
        aria-label={ariaLabel}
        suppressHydrationWarning
        data-testid="global-banner"
      >
        {bannerContent}
      </Link>
    );
  }

  return (
    <div
      className={bannerClassName}
      suppressHydrationWarning
      data-testid="global-banner"
    >
      {bannerContent}
    </div>
  );
};
